import { Reveal } from '@/components/ui/Reveal'
import { useInstallPrompt } from '@/hooks/useInstallPrompt'

import { SectionLabel } from './HowItWorks'

export function AppDownload() {
  const { canInstall, install } = useInstallPrompt()

  return (
    <section
      className="py-20"
      style={{
        background: 'var(--bg2)',
        borderBottom: '1px solid var(--lime-line)',
        borderTop: '1px solid var(--lime-line)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Left */}
        <div>
          <SectionLabel>Get the app</SectionLabel>
          <Reveal delay={0.05}>
            <h2
              className="font-display font-bold leading-[1.1] tracking-tight mb-4"
              style={{
                color: 'var(--text)',
                fontSize: 'clamp(1.9rem,3.5vw,2.8rem)',
                letterSpacing: '-.5px',
              }}
            >
              Your turf.
              <br />
              Always one tap away.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p
              className="text-[0.9rem] font-light leading-[1.75] max-w-110 mb-7"
              style={{ color: 'var(--muted)' }}
            >
              TurfChale works as a Progressive Web App — no app store needed. Open in your browser
              and add to your home screen for a full native-app experience, including offline
              support.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div
              className="flex items-start gap-3 p-4 rounded-xl"
              style={{ background: 'var(--lime-dim)', border: '1px solid var(--lime-line)' }}
            >
              <span className="text-base shrink-0">📲</span>
              <div>
                <p className="text-[0.78rem] leading-relaxed" style={{ color: 'var(--muted)' }}>
                  <strong style={{ color: 'var(--lime)' }}>Install as a PWA: </strong>
                  Open TurfChale in Chrome or Safari, tap the share icon, and choose "Add to Home
                  Screen" — works exactly like a native app, offline too.
                </p>
                {canInstall && (
                  <button
                    className="mt-2 text-[0.75rem] font-bold px-4 py-1.5 rounded-full"
                    onClick={install}
                    style={{ background: 'var(--lime)', border: 'none', color: 'var(--bg)' }}
                  >
                    Install Now
                  </button>
                )}
              </div>
            </div>
          </Reveal>
        </div>

        {/* Right — phone mockups */}
        <Reveal className="flex items-end justify-center gap-4" delay={0.15}>
          <PhoneMock />
          <PhoneMock lifted />
        </Reveal>
      </div>
    </section>
  )
}

function PhoneMock({ lifted = false }: { lifted?: boolean }) {
  return (
    <div
      className={`w-38 rounded-[20px] p-2.5 shrink-0 ${lifted ? '-translate-y-4' : ''}`}
      style={{ background: 'var(--bg3)', border: '1px solid var(--lime-line)' }}
    >
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg)' }}>
        {/* Header */}
        <div
          className="flex items-center justify-between px-2.5 py-2"
          style={{ background: 'var(--bg3)' }}
        >
          <span className="font-display font-bold text-[0.7rem]" style={{ color: 'var(--lime)' }}>
            TurfChale
          </span>
          <span
            className="w-1.5 h-1.5 rounded-full animate-[pulse-dot_2s_infinite]"
            style={{ background: 'var(--lime)' }}
          />
        </div>
        {/* Map placeholder */}
        <div className="h-18 flex items-center justify-center" style={{ background: '#0f2016' }}>
          <svg className="w-[80%] opacity-20" viewBox="0 0 100 60">
            <circle cx="50" cy="30" fill="none" r="22" stroke="#b8ec3c" strokeWidth="1" />
            <circle cx="50" cy="30" fill="#b8ec3c" r="3" />
            <line
              stroke="#b8ec3c"
              strokeDasharray="3 2"
              strokeWidth="0.8"
              x1="28"
              x2="72"
              y1="30"
              y2="30"
            />
            <line
              stroke="#b8ec3c"
              strokeDasharray="3 2"
              strokeWidth="0.8"
              x1="50"
              x2="50"
              y1="8"
              y2="52"
            />
          </svg>
        </div>
        {/* Card */}
        <div className="m-1.5 p-2 rounded-lg" style={{ background: 'var(--bg3)' }}>
          <div className="text-[0.55rem] font-semibold mb-1" style={{ color: 'var(--text)' }}>
            Champions Arena
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[0.5rem]" style={{ color: 'var(--lime)' }}>
              ₹850/hr
            </span>
            <button
              className="text-[0.45rem] font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: 'var(--lime)', border: 'none', color: 'var(--bg)' }}
            >
              Book
            </button>
          </div>
        </div>
        {/* Slots */}
        <div className="grid grid-cols-3 gap-1 mx-1.5 mb-1.5">
          {['6AM', '7AM', '8AM', '9AM', '10AM', '11AM'].map((t, i) => (
            <div
              className="rounded py-0.5 text-center text-[0.45rem]"
              key={t}
              style={
                i === 3
                  ? { background: 'var(--lime)', color: 'var(--bg)', fontWeight: 700 }
                  : i < 2
                    ? { border: '0.5px solid rgba(255,255,255,.06)', color: 'var(--text-dim)' }
                    : { border: '0.5px solid var(--lime-line)', color: 'var(--lime)' }
              }
            >
              {t}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
